import { UserX,
  Wallet,
  useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Download,
  TrendingUp,
  Users,
  DollarSign,
  PartyPopper,
  AlertTriangle,
  BarChart2,
  Loader2,
} from 'lucide-react'
import { format, subDays } from 'date-fns'
import toast from 'react-hot-toast'
import { reports, classes as classesApi } from '../api/endpoints.js'

const TABS = [
  { id: 'noshows', label: 'No-shows', icon: UserX },
  { id: 'payroll', label: 'Payroll', icon: Wallet },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart2 },
  { id: 'revenue', label: 'Revenue', icon: DollarSign },
  { id: 'attendance', label: 'Attendance', icon: TrendingUp },
  { id: 'members', label: 'Member Growth', icon: Users },
  { id: 'celebrations', label: 'Celebrations', icon: PartyPopper },
  { id: 'failed', label: 'Failed Payments', icon: AlertTriangle },
  { id: 'export', label: 'Export CSV', icon: Download },
]

function StatTile({ label, value, sub, accent = 'default' }) {
  const colors = {
    green: 'text-green-400',
    red: 'text-red-400',
    orange: 'text-primary',
    default: 'text-text-primary',
  }
  return (
    <div className="card">
      <p className="text-xs uppercase tracking-wider text-text-muted font-medium">{label}</p>
      <p className={`text-3xl font-bold mt-1 ${colors[accent]}`}>{value ?? '—'}</p>
      {sub && <p className="text-xs text-text-dim mt-0.5">{sub}</p>}
    </div>
  )
}

function DateRangePicker({ from, to, onChange }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div>
        <label className="label">From</label>
        <input
          type="date"
          className="input-field w-auto"
          value={from}
          onChange={(e) => onChange({ from: e.target.value, to })}
        />
      </div>
      <div>
        <label className="label">To</label>
        <input
          type="date"
          className="input-field w-auto"
          value={to}
          onChange={(e) => onChange({ from, to: e.target.value })}
        />
      </div>
    </div>
  )
}

function DataTable({ columns, rows, loading, emptyMsg }) {
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-10 text-text-dim text-sm">{emptyMsg ?? 'No data.'}</div>
    )
  }
  return (
    <div className="table-container">
      <table className="table-base">
        <thead className="table-head">
          <tr>
            {columns.map((c) => (
              <th key={c.key}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-surface-3">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-sm text-text-primary">
                  {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DashboardTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'dashboard'],
    queryFn: () => reports.dashboard().then((r) => r.data),
  })

  return (
    <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
      <StatTile label="Active Members" value={data?.active_members?.toLocaleString()} sub="current" accent="orange" />
      <StatTile label="MRR" value={data?.mrr != null ? `$${data.mrr.toLocaleString()}` : null} sub="monthly recurring revenue" accent="green" />
      <StatTile label="Check-ins Today" value={data?.checkins_today?.toLocaleString()} sub="gate + manual" />
      <StatTile label="Failed Payments" value={data?.failed_payments?.toLocaleString()} sub="needs attention" accent="red" />
      <StatTile label="Classes Today" value={data?.classes_today?.toLocaleString()} sub="scheduled" />
      <StatTile label="New This Month" value={data?.new_this_month?.toLocaleString()} sub="new members" accent="green" />
    </div>
  )
}

function RevenueTab() {
  const defaultFrom = format(subDays(new Date(), 30), 'yyyy-MM-dd')
  const defaultTo = format(new Date(), 'yyyy-MM-dd')
  const [range, setRange] = useState({ from: defaultFrom, to: defaultTo })

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'revenue', range],
    queryFn: () => reports.revenue(range.from, range.to).then((r) => r.data),
  })

  const rows = Array.isArray(data) ? data : data?.rows ?? data?.items ?? []

  return (
    <div className="space-y-5">
      <DateRangePicker from={range.from} to={range.to} onChange={setRange} />
      {data?.summary && (
        <div className="grid grid-cols-3 gap-4">
          <StatTile label="Total Revenue" value={`$${(data.summary.total ?? 0).toLocaleString()}`} accent="green" />
          <StatTile label="Transactions" value={data.summary.count?.toLocaleString()} />
          <StatTile label="Avg per Transaction" value={data.summary.avg != null ? `$${data.summary.avg.toFixed(2)}` : null} />
        </div>
      )}
      <DataTable
        loading={isLoading}
        emptyMsg="No revenue data for this period."
        columns={[
          { key: 'date', label: 'Date' },
          { key: 'member_name', label: 'Member' },
          { key: 'amount', label: 'Amount', render: (v) => v != null ? `$${parseFloat(v).toFixed(2)}` : '—' },
          { key: 'description', label: 'Description' },
          { key: 'status', label: 'Status' },
        ]}
        rows={rows}
      />
    </div>
  )
}

function AttendanceTab() {
  const defaultFrom = format(subDays(new Date(), 30), 'yyyy-MM-dd')
  const defaultTo = format(new Date(), 'yyyy-MM-dd')
  const [range, setRange] = useState({ from: defaultFrom, to: defaultTo })

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'attendance', range],
    queryFn: () => reports.attendance(range.from, range.to).then((r) => r.data),
  })

  const rows = Array.isArray(data) ? data : data?.rows ?? data?.items ?? []

  return (
    <div className="space-y-5">
      <DateRangePicker from={range.from} to={range.to} onChange={setRange} />
      <DataTable
        loading={isLoading}
        emptyMsg="No attendance data for this period."
        columns={[
          { key: 'date', label: 'Date' },
          { key: 'checkins', label: 'Check-ins' },
          { key: 'unique_members', label: 'Unique Members' },
          { key: 'peak_hour', label: 'Peak Hour' },
        ]}
        rows={rows}
      />
    </div>
  )
}

function MemberGrowthTab() {
  const defaultFrom = format(subDays(new Date(), 90), 'yyyy-MM-dd')
  const defaultTo = format(new Date(), 'yyyy-MM-dd')
  const [range, setRange] = useState({ from: defaultFrom, to: defaultTo })

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'members', range],
    queryFn: () => reports.members(range.from, range.to).then((r) => r.data),
  })

  const rows = Array.isArray(data) ? data : data?.rows ?? data?.items ?? []

  return (
    <div className="space-y-5">
      <DateRangePicker from={range.from} to={range.to} onChange={setRange} />
      <DataTable
        loading={isLoading}
        emptyMsg="No member data for this period."
        columns={[
          { key: 'date', label: 'Date' },
          { key: 'new_members', label: 'New Members' },
          { key: 'cancelled', label: 'Cancellations' },
          { key: 'net', label: 'Net Change' },
          { key: 'total', label: 'Running Total' },
        ]}
        rows={rows}
      />
    </div>
  )
}

function CelebrationsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'celebrations'],
    queryFn: () => reports.celebrations().then((r) => r.data),
  })

  const rows = Array.isArray(data) ? data : data?.items ?? []

  return (
    <DataTable
      loading={isLoading}
      emptyMsg="No upcoming celebrations."
      columns={[
        { key: 'member_name', label: 'Member' },
        { key: 'event_type', label: 'Event' },
        { key: 'date', label: 'Date' },
        { key: 'note', label: 'Note' },
      ]}
      rows={rows}
    />
  )
}

function FailedPaymentsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'failed-payments'],
    queryFn: () => reports.failedPayments().then((r) => r.data),
  })

  const rows = Array.isArray(data) ? data : data?.items ?? []

  return (
    <DataTable
      loading={isLoading}
      emptyMsg="No failed payments — great!"
      columns={[
        { key: 'member_name', label: 'Member' },
        { key: 'amount', label: 'Amount', render: (v) => v != null ? `$${parseFloat(v).toFixed(2)}` : '—' },
        { key: 'failed_at', label: 'Failed On' },
        { key: 'attempts', label: 'Attempts' },
        { key: 'plan_name', label: 'Plan' },
      ]}
      rows={rows}
    />
  )
}

function ExportTab() {
  const defaultFrom = format(subDays(new Date(), 30), 'yyyy-MM-dd')
  const defaultTo = format(new Date(), 'yyyy-MM-dd')
  const [range, setRange] = useState({ from: defaultFrom, to: defaultTo })
  const [exporting, setExporting] = useState(false)

  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await reports.exportCsv(range.from, range.to)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `cooppress-export-${range.from}-to-${range.to}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success('CSV exported!')
    } catch {
      toast.error('Failed to export CSV.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6 max-w-md">
      <p className="text-sm text-text-muted">
        Download a full CSV export of member data, check-ins, and revenue for the selected date range.
      </p>
      <DateRangePicker from={range.from} to={range.to} onChange={setRange} />
      <button
        onClick={handleExport}
        disabled={exporting}
        className="btn-primary flex items-center gap-2"
      >
        {exporting ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Exporting…</>
        ) : (
          <><Download className="w-4 h-4" /> Download CSV</>
        )}
      </button>
    </div>
  )
}


/** Payroll. The response shape isn't fixed in the spec, so the table is built
 *  from whatever keys come back rather than assuming column names. */
function PayrollTab() {
  const [range, setRange] = useState({ from: defaultFrom, to: defaultTo })
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'payroll', range.from, range.to],
    queryFn: () => reports.payroll(range.from, range.to).then((r) => r.data),
  })

  const rows = Array.isArray(data) ? data : data?.items ?? data?.rows ?? []
  const cols = rows.length ? Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== 'object') : []
  const pretty = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const cell = (v) =>
    typeof v === 'number'
      ? (String(v).includes('.') ? `$${v.toFixed(2)}` : v.toLocaleString())
      : v == null ? '—' : String(v)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="label">From</label>
          <input type="date" className="input-field w-auto" value={range.from}
                 onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} /></div>
        <div><label className="label">To</label>
          <input type="date" className="input-field w-auto" value={range.to}
                 onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} /></div>
      </div>

      {error ? (
        <div className="card">
          <p className="font-semibold" style={{ color: 'rgb(var(--danger))' }}>Could not load payroll</p>
          <p className="text-sm text-text-muted mt-1">
            {error?.response?.data?.detail || error.message}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table-base">
            <thead className="table-head">
              <tr>{(cols.length ? cols : ['Staff', 'Hours', 'Amount']).map((c) => (
                <th key={c}>{pretty(c)}</th>))}</tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>{Array.from({ length: cols.length || 3 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="skeleton h-4 w-3/4" /></td>))}</tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={cols.length || 3} className="px-4 py-12 text-center text-text-dim">
                  No payroll rows for this range.
                </td></tr>
              ) : rows.map((r, i) => (
                <tr key={i} className="hover:bg-surface-3/60 transition-colors">
                  {cols.map((c) => (
                    <td key={c} className="px-4 py-3 text-sm tabular-nums">{cell(r[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}


function NoShowsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['classes', 'no-shows'],
    queryFn: () => classesApi.noShows().then((r) => r.data),
  })
  const rows = Array.isArray(data) ? data : data?.items ?? []

  return (
    <div className="table-container">
      <table className="table-base">
        <thead className="table-head">
          <tr><th>Member</th><th>Class</th><th>When</th><th>Status</th></tr>
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 4 }).map((_, j) => (
                <td key={j} className="px-4 py-3"><div className="skeleton h-4 w-3/4" /></td>))}</tr>
            ))
          ) : rows.length === 0 ? (
            <tr><td colSpan={4} className="px-4 py-12 text-center text-text-dim">
              <UserX className="w-8 h-8 mx-auto mb-2 opacity-30" />No class no-shows recorded.
            </td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id ?? i} className="hover:bg-surface-3/60 transition-colors">
              <td className="px-4 py-3 text-sm text-text-primary">
                {r.member?.full_name ?? r.member_name ?? r.member_id ?? '—'}
              </td>
              <td className="px-4 py-3 text-sm text-text-muted">
                {r.gym_class?.name ?? r.class_name ?? '—'}
              </td>
              <td className="px-4 py-3 text-sm text-text-muted">
                {r.gym_class?.start_time
                  ? format(new Date(r.gym_class.start_time), 'MMM d, h:mm a')
                  : r.booked_at ? format(new Date(r.booked_at), 'MMM d, h:mm a') : '—'}
              </td>
              <td className="px-4 py-3"><span className="badge-suspended">{r.status ?? 'no show'}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TAB_COMPONENTS = {
  noshows: NoShowsTab,
  payroll: PayrollTab,
  dashboard: DashboardTab,
  revenue: RevenueTab,
  attendance: AttendanceTab,
  members: MemberGrowthTab,
  celebrations: CelebrationsTab,
  failed: FailedPaymentsTab,
  export: ExportTab,
}

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('dashboard')
  const ActiveComponent = TAB_COMPONENTS[activeTab]

  return (
    <div className="space-y-5">
      <h1 className="page-title">Reports</h1>

      {/* Tab strip */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 ${
              activeTab === id
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text-primary hover:bg-surface-3'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div className="animate-fade-in">
        {ActiveComponent && <ActiveComponent />}
      </div>
    </div>
  )
}
